import React, { useState, useEffect } from 'react';
import SlotCourtInfo from './SlotCourtInfo';
import { bookings } from '../../api';
import LoadingSkeleton from '../LoadingSkeleton';
import './Bookings.css';

export default function BookingRequestsTab({ onBookingChanged, navTarget = null }) {
  const [bookingRequests, setBookingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [responding, setResponding] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    pending: true,
    approved: false,
    denied: false,
    canceled: false
  });
  const [showSlotActionModal, setShowSlotActionModal] = useState(false);
  const [bookingToDeny, setBookingToDeny] = useState(null);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const response = await bookings.getIncomingBookings();
      setBookingRequests(response.data.bookings || []);
      setError(null);
    } catch (err) {
      console.error('❌ Error loading bookings:', err);
      setError('Failed to load booking requests');
    } finally {
      setLoading(false);
    }
  };

  // Notification tap about an incoming request: open "pending" and bring it into view
  useEffect(() => {
    if (!navTarget || navTarget.tab !== 'bookings' || navTarget.section !== 'requests' || loading) return undefined;
    setExpandedSections(prev => ({ ...prev, pending: true }));
    const timer = setTimeout(() => {
      const el = document.getElementById('booking-requests-anchor');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('note-focus-flash');
        setTimeout(() => el.classList.remove('note-focus-flash'), 3500);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [navTarget, loading]);

  const toggleSection = (status) => {
    setExpandedSections(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  // Group requests by status
  const requestsByStatus = {
    pending: bookingRequests.filter(req => req.status === 'pending'),
    approved: bookingRequests.filter(req => req.status === 'approved'),
    denied: bookingRequests.filter(req => req.status === 'denied'),
    canceled: bookingRequests.filter(req => req.status === 'canceled')
  };

  const pendingCount = requestsByStatus.pending.length;
  const statusOrder = ['pending', 'approved', 'denied', 'canceled'];

  const handleApprove = async () => {
    if (!selectedRequest) return;

    setResponding(true);
    try {
      await bookings.approveBooking(selectedRequest._id, responseMessage);
      // Update local state
      setBookingRequests(prevs =>
        prevs.map(req =>
          req._id === selectedRequest._id
            ? { ...req, status: 'approved', responseMessage, respondedAt: new Date() }
            : req
        )
      );
      setSelectedRequest(null);
      setResponseMessage('');
      // Approving can auto-fill a session on the player's active coaching
      // pack - let the coach's Coaching Plans list (a sibling component
      // that doesn't share this state) know to refresh so it doesn't keep
      // showing that session as unscheduled.
      if (onBookingChanged) onBookingChanged();
    } catch (err) {
      console.error('❌ Error approving booking:', err);
      alert('Failed to approve booking');
    } finally {
      setResponding(false);
    }
  };

  // A coach can still cancel a lesson after already confirming it - plans
  // change. Canceling here (rather than only being able to approve/deny
  // while pending) reuses the same cancel endpoint a player/coach uses
  // elsewhere; if it was counting toward a coaching pack, that gets marked
  // canceled (not silently erased) and the player gets a fresh makeup
  // session instead of losing the credit.
  const handleCancelApproved = async () => {
    if (!selectedRequest) return;

    setResponding(true);
    try {
      await bookings.cancelBooking(selectedRequest._id, responseMessage || 'Canceled by coach');
      setBookingRequests(prevs =>
        prevs.map(req =>
          req._id === selectedRequest._id
            ? { ...req, status: 'canceled', responseMessage, respondedAt: new Date() }
            : req
        )
      );
      setSelectedRequest(null);
      setResponseMessage('');
      if (onBookingChanged) onBookingChanged();
    } catch (err) {
      console.error('❌ Error canceling booking:', err);
      alert('Failed to cancel booking');
    } finally {
      setResponding(false);
    }
  };

  const handleDeny = () => {
    if (!selectedRequest) return;
    // Show modal to ask coach about slot action
    setBookingToDeny(selectedRequest);
    setShowSlotActionModal(true);
  };

  const confirmDeny = async (slotAction) => {
    if (!bookingToDeny) return;

    setResponding(true);
    setShowSlotActionModal(false);
    try {
      await bookings.denyBooking(bookingToDeny._id, responseMessage, slotAction);
      // Update local state
      setBookingRequests(prevs =>
        prevs.map(req =>
          req._id === bookingToDeny._id
            ? { ...req, status: 'denied', responseMessage, respondedAt: new Date() }
            : req
        )
      );
      setSelectedRequest(null);
      setResponseMessage('');
      setBookingToDeny(null);
    } catch (err) {
      console.error('❌ Error denying booking:', err);
      alert('Failed to deny booking');
      setBookingToDeny(null);
    } finally {
      setResponding(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'approved': return '✅';
      case 'denied': return '❌';
      case 'canceled': return '⛔';
      default: return '❓';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'approved': return '#27ae60';
      case 'denied': return '#e74c3c';
      case 'canceled': return '#95a5a6';
      default: return '#3498db';
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      meridiem: 'short'
    });
  };

  if (loading) {
    return (
      <div className="booking-tab-content">
        <div style={{ padding: '20px 0' }}>
          <LoadingSkeleton type="card" count={3} />
        </div>
      </div>
    );
  }

  return (
    <div className="booking-tab-content" id="booking-requests-anchor">
      <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>
        📥 Booking Requests
        {pendingCount > 0 && (
          <span style={{
            marginLeft: '10px',
            padding: '2px 8px',
            background: '#f39c12',
            color: 'white',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            {pendingCount} Pending
          </span>
        )}
      </h3>

      {error && (
        <div style={{
          padding: '12px',
          background: '#ffe6e6',
          border: '1px solid #e74c3c',
          borderRadius: '4px',
          color: '#c0392b',
          marginBottom: '15px'
        }}>
          ⚠️ {error}
        </div>
      )}

      {bookingRequests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>📭</div>
          No booking requests yet
        </div>
      ) : (
        <div>
          {statusOrder.map(status => {
            const requests = requestsByStatus[status];
            const isExpanded = expandedSections[status];

            return (
              <div key={status} style={{ marginBottom: '12px' }}>
                {/* Collapsible section header */}
                <button
                  onClick={() => toggleSection(status)}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    background: status === 'pending' ? '#fff3cd' : status === 'approved' ? '#d4edda' : status === 'denied' ? '#f8d7da' : '#e2e3e5',
                    border: '1px solid ' + (status === 'pending' ? '#ffc107' : status === 'approved' ? '#28a745' : status === 'denied' ? '#dc3545' : '#999'),
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: status === 'pending' ? '#856404' : status === 'approved' ? '#155724' : status === 'denied' ? '#721c24' : '#383d41',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  <span>
                    {getStatusIcon(status)} {status.charAt(0).toUpperCase() + status.slice(1)} ({requests.length})
                  </span>
                  <span style={{ fontSize: '16px' }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                </button>

                {/* Expanded section content */}
                {isExpanded && (
                  <div className="booking-requests-list" style={{ marginTop: '8px' }}>
                    {requests.length === 0 ? (
                      <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#999',
                        fontSize: '13px',
                        background: '#f9f9f9',
                        borderRadius: '4px'
                      }}>
                        No {status} booking requests
                      </div>
                    ) : (
                      requests.map(req => (
            <div
              key={req._id}
              className="booking-request-card"
              style={{
                borderLeft: `4px solid ${getStatusColor(req.status)}`,
                background: selectedRequest?._id === req._id ? '#f0f7ff' : 'white',
                cursor: 'pointer'
              }}
              onClick={() => setSelectedRequest(selectedRequest?._id === req._id ? null : req)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#2c3e50',
                    marginBottom: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {getStatusIcon(req.status)} {req.requesterName}
                    {req.isGuestBooking && (
                      <span style={{
                        background: '#e8f4f8',
                        color: '#0288d1',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}>
                        🌐 GUEST
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px' }}>
                    📧 {req.requesterEmail}
                  </div>
                  {req.isGuestBooking && req.guestPhone && (
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px' }}>
                      📱 {req.guestPhone}
                    </div>
                  )}
                  <div style={{ fontSize: '13px', color: '#34495e', marginBottom: '5px' }}>
                    <strong>{req.slotTitle}</strong>
                  </div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px' }}>
                    🕐 {formatTime(req.scheduledStartTime)}
                  </div>
                  <SlotCourtInfo slot={req.slotId} margin="0 0 5px" />
                  {(req.isGuestBooking || req.partySize > 1) && req.partySize && (
                    <div style={{ fontSize: '12px', color: '#27ae60', fontWeight: 'bold', marginBottom: '5px' }}>
                      👥 Party of {req.partySize}
                    </div>
                  )}
                  {req.playerNames && req.playerNames.length > 0 && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px',
                      background: '#e8f5e9',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#2e7d32',
                      borderLeft: '3px solid #27ae60'
                    }}>
                      <strong>Players:</strong>
                      {req.playerNames.map((name, idx) => (
                        <div key={idx} style={{ marginTop: idx === 0 ? '4px' : '2px' }}>
                          {idx + 1}. {name}
                        </div>
                      ))}
                    </div>
                  )}
                  {req.message && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px',
                      background: '#f8f9fa',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#555',
                      fontStyle: 'italic'
                    }}>
                    💬 "{req.message}"
                    </div>
                  )}
                </div>

                <div style={{ textAlign: 'right', minWidth: '80px' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    background:
                      req.status === 'pending' ? '#fff3cd' :
                      req.status === 'approved' ? '#d4edda' :
                      req.status === 'denied' ? '#f8d7da' :
                      '#e2e3e5',
                    color:
                      req.status === 'pending' ? '#856404' :
                      req.status === 'approved' ? '#155724' :
                      req.status === 'denied' ? '#721c24' :
                      '#383d41',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {req.status}
                  </span>
                </div>
              </div>

              {/* Expanded request details */}
              {selectedRequest?._id === req._id && req.status === 'pending' && (
                <div style={{
                  marginTop: '15px',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <textarea
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    placeholder="Optional: Add a message for the requester (e.g., confirmation details, location, what to prepare)"
                    maxLength="500"
                    rows="3"
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontFamily: 'inherit',
                      fontSize: '13px'
                    }}
                  />

                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setSelectedRequest(null);
                        setResponseMessage('');
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeny}
                      disabled={responding}
                      style={{
                        padding: '8px 16px',
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: responding ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        opacity: responding ? 0.6 : 1
                      }}
                    >
                      {responding ? '⏳ Denying...' : '❌ Deny'}
                    </button>
                    <button
                      onClick={handleApprove}
                      disabled={responding}
                      style={{
                        padding: '8px 16px',
                        background: '#27ae60',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: responding ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        opacity: responding ? 0.6 : 1
                      }}
                    >
                      {responding ? '⏳ Approving...' : '✅ Approve'}
                    </button>
                  </div>
                </div>
              )}

              {/* A confirmed lesson can still be canceled - plans change */}
              {selectedRequest?._id === req._id && req.status === 'approved' && (
                <div style={{
                  marginTop: '15px',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <textarea
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    placeholder="Optional: Let the player know why (e.g., 'Something came up, let's reschedule')"
                    maxLength="500"
                    rows="3"
                    style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontFamily: 'inherit',
                      fontSize: '13px'
                    }}
                  />

                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setSelectedRequest(null);
                        setResponseMessage('');
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleCancelApproved}
                      disabled={responding}
                      style={{
                        padding: '8px 16px',
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: responding ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        opacity: responding ? 0.6 : 1
                      }}
                    >
                      {responding ? '⏳ Canceling...' : '❌ Cancel Lesson'}
                    </button>
                  </div>
                </div>
              )}

              {/* Show response message if already responded */}
              {req.status !== 'pending' && req.responseMessage && (
                <div style={{
                  marginTop: '10px',
                  padding: '8px',
                  background: getStatusColor(req.status) + '20',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#2c3e50',
                  borderLeft: `3px solid ${getStatusColor(req.status)}`
                }}>
                  <strong>Your response:</strong> {req.responseMessage}
                </div>
              )}
            </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Slot Action Modal */}
      {showSlotActionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#2c3e50',
              marginBottom: '15px',
              fontSize: '18px'
            }}>
              ❌ Deny Booking
            </h3>

            <p style={{
              color: '#666',
              marginBottom: '25px',
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              What would you like to do with this time slot?
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <button
                onClick={() => confirmDeny('available')}
                disabled={responding}
                style={{
                  padding: '12px',
                  background: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: responding ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: responding ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                ♻️ Put Back to Available
              </button>

              <p style={{
                color: '#999',
                fontSize: '12px',
                margin: '0 0 10px 0'
              }}>
                Keep the slot available for others to book
              </p>

              <button
                onClick={() => confirmDeny('close')}
                disabled={responding}
                style={{
                  padding: '12px',
                  background: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: responding ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: responding ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                🔒 Close/Delete Slot
              </button>

              <p style={{
                color: '#999',
                fontSize: '12px',
                margin: '0'
              }}>
                Remove this slot - no one can book it
              </p>
            </div>

            <button
              onClick={() => {
                setShowSlotActionModal(false);
                setBookingToDeny(null);
              }}
              disabled={responding}
              style={{
                padding: '8px 16px',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
